import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Scale, ArrowRight, ArrowLeft, CheckCircle2, ClipboardCheck, Gift, ShieldCheck, FileCheck2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ComplianceStrip from "../components/ComplianceStrip";
import NextStepPromptCard from "../components/NextStepPromptCard";
import PartnerConsentDialog from "../components/PartnerConsentDialog";
import Seo from "../components/Seo";
import { api, track } from "../lib/api";
import { fireMetaLeadOnce } from "../lib/metaPixel";

/* IMPORTANT — the RG Law conveyancing integration is unchanged.
   This is a presentation-layer + analytics refactor only. The form still
   collects the exact same fields and still POSTs the identical object
   ({ ...form, purchase_price: number, consent: true, source }) to
   /conveyancing-lead. No field names, destination, consent wording, reference
   handling or submission mechanism have changed. */

const TITLES = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof", "Mx"];
const TRANSACTION_TYPES = [
  "Standard purchase",
  "New build purchase",
  "Remortgage",
  "Sale only",
  "Sale and purchase",
  "Other / not sure",
];

const initialForm = {
  transaction_type: "",
  title: "",
  full_name: "",
  contact_number: "",
  email: "",
  property_address: "",
  purchase_price: "",
  notes: "",
};

const NEXT_STEPS = [
  "Our selected conveyancing provider will review your details and prepare a quote.",
  "They'll be in touch using the details you provided — keep an eye on your email and phone.",
  "There's no commitment: you decide whether to go ahead once you've seen the quote.",
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const STEP_TITLES = ["Your move", "Your details", "Final details"];

const ErrorText = ({ msg }) =>
  msg ? <span className="block mt-1 text-xs text-[#B91C1C]" role="alert">{msg}</span> : null;

/* ---------- Hero ---------- */
const REASSURANCE = [
  { icon: FileCheck2, text: "No-obligation conveyancing quote" },
  { icon: ShieldCheck, text: "Regulated conveyancing provider" },
  { icon: Gift, text: "£50 MortgagePath cashback after completion" },
];

const Hero = ({ onStart }) => (
  <section className="rounded-2xl border border-[#0F766E]/20 bg-gradient-to-br from-[#0F766E]/6 via-white to-[#10B981]/8 p-6 sm:p-8" data-testid="cq-hero">
    <div className="text-xs uppercase tracking-widest text-[#0F766E] flex items-center gap-2">
      <Scale className="w-3.5 h-3.5" /> Conveyancing quote
    </div>
    <h1 className="font-sora mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B1220]">
      Get a <span className="text-[#0B5F58]">£50 cashback</span> when you complete your conveyancing through MortgagePath
    </h1>
    <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl">
      Request your conveyancing quote through MortgagePath. There's no obligation, and if you choose to proceed and
      complete your conveyancing through our selected provider, MortgagePath will pay you £50 cashback. Eligibility and T&amp;Cs apply.
    </p>
    <ul className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-x-6 sm:gap-y-2">
      {REASSURANCE.map(({ icon: Icon, text }) => (
        <li key={text} className="flex items-center gap-2 text-sm text-[#0B1220]">
          <Icon className="w-4 h-4 text-[#0F766E] shrink-0" aria-hidden="true" />
          <span>{text}</span>
        </li>
      ))}
    </ul>
    <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
      <button type="button" onClick={onStart} className="mp-btn-primary w-full sm:w-auto justify-center" data-testid="cq-hero-cta">
        Get my conveyancing quote <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-xs text-[#94A3B8] leading-relaxed">
        The £50 cashback is a separate MortgagePath reward and doesn't affect the conveyancing service.{" "}
        <Link to="/reward-terms" className="underline hover:text-[#0F766E]" data-testid="cq-reward-terms">Cashback terms</Link>.
      </p>
    </div>
  </section>
);

/* ---------- Progress ---------- */
const ProgressBar = ({ step }) => (
  <div data-testid="cq-progress">
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold text-[#0B1220]">Step {step} of 3</span>
      <span className="text-xs text-[#475569]">{STEP_TITLES[step - 1]}</span>
    </div>
    <div className="mt-2 flex gap-1.5" aria-hidden="true">
      {[1, 2, 3].map((n) => (
        <span key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-[#0F766E]" : "bg-[#DCE7E3]"}`} />
      ))}
    </div>
  </div>
);

/* ---------- Step panels (same fields, grouped) ---------- */
const StepMove = ({ form, set, errors }) => (
  <div className="grid gap-5">
    <label className="block">
      <span className="mp-label">Purchase / transaction type <span className="text-[#0F766E]">*</span></span>
      <select className="mp-input" value={form.transaction_type} aria-invalid={errors.transaction_type ? "true" : undefined} onChange={(e) => set("transaction_type", e.target.value)} data-testid="cq-transaction_type">
        <option value="">Select…</option>
        {TRANSACTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <ErrorText msg={errors.transaction_type} />
    </label>
    <label className="block">
      <span className="mp-label">Property address <span className="text-[#0F766E]">*</span></span>
      <textarea className="mp-input" rows={2} value={form.property_address} placeholder="Street, town, county, postcode" aria-invalid={errors.property_address ? "true" : undefined} onChange={(e) => set("property_address", e.target.value)} data-testid="cq-property_address" />
      <ErrorText msg={errors.property_address} />
    </label>
    <label className="block">
      <span className="mp-label">Purchase price / estimated property value (£) <span className="text-[#0F766E]">*</span></span>
      <input type="text" inputMode="numeric" className="mp-input" value={form.purchase_price} placeholder="e.g. 300,000" aria-invalid={errors.purchase_price ? "true" : undefined} onChange={(e) => set("purchase_price", e.target.value.replace(/[^0-9.]/g, ""))} data-testid="cq-purchase_price" />
      <span className="mp-help">Helps the provider prepare an accurate quote.</span>
      <ErrorText msg={errors.purchase_price} />
    </label>
  </div>
);

const StepDetails = ({ form, set, errors }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    <label className="block">
      <span className="mp-label">Title</span>
      <select className="mp-input" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="cq-title">
        <option value="">Optional</option>
        {TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
    </label>
    <div className="hidden sm:block" aria-hidden="true" />
    <label className="block sm:col-span-2">
      <span className="mp-label">Full name <span className="text-[#0F766E]">*</span></span>
      <input type="text" className="mp-input" autoComplete="name" value={form.full_name} placeholder="e.g. Alex Taylor" aria-invalid={errors.full_name ? "true" : undefined} onChange={(e) => set("full_name", e.target.value)} data-testid="cq-full_name" />
      <ErrorText msg={errors.full_name} />
    </label>
    <label className="block">
      <span className="mp-label">Contact number <span className="text-[#0F766E]">*</span></span>
      <input type="tel" inputMode="tel" autoComplete="tel" className="mp-input" value={form.contact_number} placeholder="e.g. 07123 456789" aria-invalid={errors.contact_number ? "true" : undefined} onChange={(e) => set("contact_number", e.target.value)} data-testid="cq-contact_number" />
      <ErrorText msg={errors.contact_number} />
    </label>
    <label className="block">
      <span className="mp-label">Email address <span className="text-[#0F766E]">*</span></span>
      <input type="email" inputMode="email" autoComplete="email" className="mp-input" value={form.email} placeholder="you@example.com" aria-invalid={errors.email ? "true" : undefined} onChange={(e) => set("email", e.target.value)} data-testid="cq-email" />
      <ErrorText msg={errors.email} />
    </label>
  </div>
);

const StepFinal = ({ form, set }) => (
  <div className="grid gap-5">
    <label className="block">
      <span className="mp-label">Message / additional notes (optional)</span>
      <textarea className="mp-input" rows={3} value={form.notes} placeholder="e.g. leasehold flat, chain length, target completion date" onChange={(e) => set("notes", e.target.value)} data-testid="cq-notes" />
      <span className="mp-help">Anything that would help the provider prepare your quote.</span>
    </label>
    <p className="text-xs text-[#475569] leading-relaxed">
      MortgagePath is not a conveyancer and does not provide legal advice. Conveyancing is provided by our selected
      regulated provider. MortgagePath may receive a referral fee if you choose to proceed. The £50 cashback is a separate
      MortgagePath promotion. Nothing is shared with a third party until you confirm consent on the next screen.
    </p>
  </div>
);

/* ---------- Outcome views ---------- */
const SuccessView = ({ reference }) => (
  <div className="mt-8 mp-card p-8 sm:p-10 text-center" data-testid="cq-success">
    <div className="mx-auto w-14 h-14 rounded-full bg-[#10B981]/10 grid place-items-center">
      <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
    </div>
    <h2 className="font-sora text-3xl font-bold text-[#0B1220] mt-4 tracking-tight">Your conveyancing quote request has been sent</h2>
    <p className="mt-3 text-[#475569] leading-relaxed max-w-xl mx-auto">
      We've sent your enquiry to our selected conveyancing provider. They should contact you directly about your quote.
    </p>

    <div className="mt-6 inline-flex flex-col items-center gap-1 rounded-xl bg-[#F7FAF9] border border-[#DCE7E3] px-6 py-4">
      <span className="text-xs uppercase tracking-widest text-[#0F766E]">Your MortgagePath reference</span>
      <span className="font-sora text-lg font-semibold text-[#0B1220]" data-testid="cq-reference">{reference}</span>
      <span className="text-xs text-[#475569]">Keep this for your records.</span>
    </div>

    <div className="mt-8 max-w-md mx-auto text-left rounded-2xl border border-[#DCE7E3] bg-white p-6" data-testid="cq-next-steps">
      <div className="text-xs uppercase tracking-widest text-[#0F766E]">What happens next</div>
      <ul className="mt-3 space-y-2.5">
        {NEXT_STEPS.map((s) => (
          <li key={s.slice(0, 18)} className="flex gap-3 text-sm text-[#475569] leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#10B981] shrink-0" aria-hidden="true" />
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>

    <div className="mt-6 max-w-md mx-auto flex items-start gap-3 rounded-xl bg-[#0F766E]/5 border border-[#0F766E]/15 px-5 py-4 text-left">
      <Gift className="w-5 h-5 text-[#0F766E] shrink-0 mt-0.5" aria-hidden="true" />
      <p className="text-sm text-[#475569] leading-relaxed">
        If you proceed and complete your conveyancing, remember to claim your <strong className="text-[#0B1220]">£50 MortgagePath cashback</strong>, subject to the cashback terms.{" "}
        <Link to="/contact" className="text-[#0F766E] underline" data-testid="cq-how-to-claim">How to claim</Link>{" · "}
        <Link to="/reward-terms" className="text-[#0F766E] underline" data-testid="cq-success-terms">Cashback terms</Link>.
      </p>
    </div>

    {/* Optional cross-sell — deliberate opt-in only; no data is shared automatically */}
    <div className="mt-6 max-w-md mx-auto">
      <NextStepPromptCard
        icon={ClipboardCheck}
        eyebrow="Still need a property survey?"
        title="Get a property survey quote"
        body="MortgagePath can also help you request a property survey quote. Complete a qualifying survey through MortgagePath and you can qualify for a separate £50 cashback."
        to="/survey-quote?source=post_conveyancing_next_step"
        ctaEvent="survey_cta_clicked"
        ctaLocation="post_conveyancing_next_step"
      />
    </div>

    <div className="mt-8 flex flex-wrap gap-3 justify-center">
      <Link to="/first-time-buyer-guide" className="mp-btn-secondary text-sm" data-testid="cq-guide">Read the buying guide</Link>
      <Link to="/" className="mp-btn-primary text-sm" data-testid="cq-home">Back to home <ArrowRight className="w-4 h-4" /></Link>
    </div>
  </div>
);

const FailedView = ({ onRetry }) => (
  <div className="mt-8 mp-card p-8 sm:p-10 text-center" data-testid="cq-failed">
    <div className="mx-auto w-14 h-14 rounded-full bg-[#B45309]/10 grid place-items-center">
      <AlertTriangle className="w-7 h-7 text-[#B45309]" />
    </div>
    <h2 className="font-sora text-2xl sm:text-3xl font-bold text-[#0B1220] mt-4 tracking-tight">We couldn't confirm your request</h2>
    <p className="mt-3 text-[#475569] leading-relaxed max-w-xl mx-auto">
      Something went wrong sending your request to our conveyancing provider, so we can't confirm it was received.
      Please try again in a few minutes. If it keeps happening, email us at{" "}
      <a href="mailto:info@mortgagepath.uk" className="text-[#0F766E] underline">info@mortgagepath.uk</a> and we'll help.
    </p>
    <div className="mt-6 flex flex-wrap gap-3 justify-center">
      <button type="button" onClick={onRetry} className="mp-btn-primary" data-testid="cq-retry">Try again <ArrowRight className="w-4 h-4" /></button>
      <Link to="/" className="mp-btn-secondary" data-testid="cq-failed-home">Back to home</Link>
    </div>
  </div>
);

/* ===================================================================== */
const ConveyancingQuote = () => {
  const [params] = useSearchParams();
  const source = params.get("source") || "direct";
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const [failed, setFailed] = useState(false);
  const startedRef = useRef(false);
  const formRef = useRef(null);

  useEffect(() => { track("conveyancing_quote_view", { source }); }, [source]);

  const markStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      track("conveyancing_quote_start", { source });
    }
  };

  const set = (name, value) => {
    markStarted();
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const onHeroStart = () => {
    markStarted();
    scrollToForm();
    setTimeout(() => {
      document.querySelector("#cq-step-region input, #cq-step-region select, #cq-step-region textarea")?.focus();
    }, 420);
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.transaction_type) e.transaction_type = "Please choose a transaction type.";
      if (!String(form.property_address).trim()) e.property_address = "Please enter the property address.";
      if (!(+form.purchase_price > 0)) e.purchase_price = "Please enter the purchase price or estimated value.";
    } else if (s === 2) {
      if (!String(form.full_name).trim()) e.full_name = "Please enter your full name.";
      if (!String(form.contact_number).trim()) e.contact_number = "Please enter a contact number.";
      if (!String(form.email).trim()) e.email = "Please enter your email address.";
      else if (!EMAIL_RE.test(form.email)) e.email = "Please enter a valid email address.";
    }
    return e;
  };

  const goNext = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length) {
      setErrors((e) => ({ ...e, ...errs }));
      toast.error("Please complete the required fields.");
      return;
    }
    track(`conveyancing_quote_step_${step}_complete`, { source });
    setStep((s) => s + 1);
    scrollToForm();
  };

  const goBack = () => { setStep((s) => Math.max(1, s - 1)); scrollToForm(); };

  const onRequestQuote = (e) => {
    e.preventDefault();
    // Validate steps 1 and 2 (step 3 has no required fields) before confirming.
    const errs = { ...validateStep(1), ...validateStep(2) };
    if (Object.keys(errs).length) {
      setErrors((err) => ({ ...err, ...errs }));
      if (errs.transaction_type || errs.property_address || errs.purchase_price) setStep(1);
      else setStep(2);
      toast.error("Please complete the required fields.");
      return;
    }
    setConfirmOpen(true);
  };

  /* Only here do we transmit to the RG Law referral integration. The payload
     is identical to the original single-page form. */
  const onConfirmedSubmit = async () => {
    setSubmitting(true);
    track("conveyancing_quote_submit", { source, transaction_type: form.transaction_type });
    try {
      const payload = { ...form, purchase_price: +form.purchase_price || 0, consent: true, source };
      const { data } = await api.post("/conveyancing-lead", payload);

      if (data.partner_email_sent === true) {
        // conveyancing_quote_success fires ONLY when RG Law delivery is genuinely confirmed.
        track("conveyancing_quote_success", { source, lead_reference: data.reference, transaction_type: form.transaction_type });
        // Meta standard Lead — fired once per confirmed RG Law delivery only.
        fireMetaLeadOnce(data.reference);
        setReference(data.reference);
        setConfirmOpen(false);
        toast.success("Your conveyancing enquiry has been sent.");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        // Delivery to RG Law not confirmed — must NOT show success.
        setConfirmOpen(false);
        setFailed(true);
        track("conveyancing_quote_error", { source, status: "delivery_not_confirmed" });
        toast.error("We couldn't confirm your request reached the conveyancing provider.");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries and try again.");
      track("conveyancing_quote_error", { source, status: status || 0 });
    } finally {
      setSubmitting(false);
    }
  };

  const onRetry = () => { setFailed(false); setStep(1); setTimeout(scrollToForm, 50); };

  const showForm = !reference && !failed;

  return (
    <div className="mp-container py-10 sm:py-14 max-w-3xl">
      <Seo
        title="Request a Conveyancing Quote | MortgagePath"
        description="Request a conveyancing quote from our selected conveyancing provider. Educational referral only — MortgagePath is not a conveyancer and does not provide conveyancing advice."
        path="/conveyancing-quote"
        noindex
      />

      {reference && <SuccessView reference={reference} />}
      {failed && <FailedView onRetry={onRetry} />}

      {showForm && (
        <>
          <Hero onStart={onHeroStart} />

          <div ref={formRef} className="mt-8 scroll-mt-20" id="conveyancing-form">
            <div className="mp-card p-6 sm:p-7">
              <ProgressBar step={step} />
              <h2 className="font-sora text-lg font-semibold text-[#0B1220] mt-6">{STEP_TITLES[step - 1]}</h2>

              <form
                className="mt-5"
                id="cq-step-region"
                onSubmit={step === 3 ? onRequestQuote : (e) => { e.preventDefault(); goNext(); }}
                noValidate
                data-testid="cq-form"
              >
                {step === 1 && <StepMove form={form} set={set} errors={errors} />}
                {step === 2 && <StepDetails form={form} set={set} errors={errors} />}
                {step === 3 && <StepFinal form={form} set={set} />}

                <div className="mt-6 flex items-center justify-between gap-3">
                  {step > 1 ? (
                    <button type="button" onClick={goBack} className="mp-btn-secondary" data-testid="cq-back">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                  ) : <span />}

                  {step < 3 ? (
                    <button type="submit" className="mp-btn-primary" data-testid="cq-next">
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button type="submit" className="mp-btn-primary" data-testid="cq-submit">
                      Request my conveyancing quote <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="mt-4 flex items-start gap-3 rounded-xl border border-[#DCE7E3] bg-[#F7FAF9] px-5 py-4" data-testid="cq-trust">
              <ShieldCheck className="w-5 h-5 text-[#0F766E] shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-[#475569] leading-relaxed">
                MortgagePath helps homebuyers understand their options and access trusted UK property services. Your
                conveyancing will be provided by our selected regulated provider.
              </p>
            </div>
          </div>
        </>
      )}

      <PartnerConsentDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        providerName="RG Law"
        headline="Confirm your quote request"
        summary="our selected conveyancing provider, so they can contact you about your conveyancing quote"
        checkboxLabel={
          <>
            I consent to MortgagePath sharing the details I have provided with{" "}
            <span className="font-semibold">RG Law</span> so they can contact me about my conveyancing
            quote. I understand that MortgagePath may receive a referral fee if I choose to proceed. See our{" "}
            <Link to="/privacy" className="text-[#0F766E] underline">Privacy Policy</Link>.
          </>
        }
        submitting={submitting}
        onConfirm={onConfirmedSubmit}
        testidPrefix="cq-consent"
      />

      <div className="mt-10"><ComplianceStrip /></div>
    </div>
  );
};

export default ConveyancingQuote;
